import "./LabAttachmentsEditComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {CommonService} from "../../../../../shared/services";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import _ from "lodash";
import {ImageConfig} from "../../../../../constants";
import InputComponent from "../../../../../shared/components/core/input/InputComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import AttachmentViewDownloadComponent
    from "../../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import DialogComponent from "../../../../../shared/components/dialog/dialogComponent";
import FilePreviewerComponent from "../../../../../shared/components/file-previewer/filePreviewerComponent";
import PdfPreviewComponent from "../../../../../shared/components/pdf-preview/pdfPreviewComponent";
import ErrorComponent from "../../../../../shared/components/form-controls/error/errorComponent";

interface LabAttachmentsEditComponentProps {
    labID: any;
    labAttachments: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const LabAttachmentsEditComponent = (props: LabAttachmentsEditComponentProps) => {
    const {labID, labAttachments, onUpdateComplete, onEditCancel} = props;

    const labAttachmentFileRef = useRef<HTMLInputElement | null>(null);
    const [labAttachmentList, setLabAttachmentList] = useState<any[]>([]);
    const [labAttachmentFile, setLabAttachmentFile] = useState<any>(undefined);
    const [labAttachmentTitle, setLabAttachmentTitle] = useState<string>("");
    const [isContinueVisible, setIsContinueVisible] = useState<boolean>(false);
    const [isLabAttachmentBeingUploaded, setIsLabAttachmentBeingUploaded] = useState<boolean>(false);
    const [showLabAttachmentPreviewDialog, setShowLabAttachmentPreviewDialog] = useState<boolean>(false);
    const [isNewLabAttachment, setNewLabAttachment] = useState<boolean>(false);
    const [isEdited, setIsEdited] = useState<boolean>(false);

    const handleLabAttachmentSuccess = useCallback((response: any) => {
        setIsEdited(true)
        CommonService._alert.showToast(response?.message || 'Added Lab Attachments', "success");
        setLabAttachmentList((oldState: any) => {
            if (oldState?.length > 0) {
                return [response.data, ...oldState]
            } else {
                return [response.data]
            }
        });
        setIsLabAttachmentBeingUploaded(false);
        setLabAttachmentFile(undefined);
        setLabAttachmentTitle("");

    }, []);

    const handleLabAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = '';
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setIsContinueVisible(true);
        setShowLabAttachmentPreviewDialog(true);
        setLabAttachmentFile(file);
        setNewLabAttachment(true);
    }, []);

    const onLabAttachmentSubmit = useCallback(() => {
        const formData = new FormData();
        if (labAttachmentFile) {
            formData.append('image', labAttachmentFile);
            formData.append('attachmentType', 'lab')
        }
        formData.append('attachmentName', labAttachmentTitle);
        setIsLabAttachmentBeingUploaded(true);
        CommonService._lab.addLabAttachment(labID, formData)
            .then((response: IAPIResponseType<any>) => {
                handleLabAttachmentSuccess(response);
                setShowLabAttachmentPreviewDialog(false);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error uploading lab attachment', "error");
                setIsLabAttachmentBeingUploaded(false);
            })
    }, [labAttachmentFile, handleLabAttachmentSuccess, labAttachmentTitle, labID]);

    const removeLabAttachment = useCallback((attachmentId: any) => {
        const payload = {};
        CommonService._lab.deleteLabAttachment(labID, attachmentId, payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response?.message || 'Lab attachment has been removed', "success");
                setLabAttachmentList((oldState: any) => {
                    const updatedList = _.cloneDeep(oldState);
                    const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                    updatedList.splice(index, 1);
                    return updatedList;
                })
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error deleting lab attachment', "error");
            })
    }, [labID]);

    const handleAttachmentRemove = useCallback((id: any, type: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to delete this attachment?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.DeleteImage}`,
            direction: "up",
            yes: {
                text: "Confirm",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                removeLabAttachment(id);
            })
            .catch(() => {
            })
    }, [removeLabAttachment]);

    useEffect(() => {
        setLabAttachmentList(labAttachments);
    }, [labAttachments])

    return (
        <div className="lab-attachment-screen screen details-tab-data-content">
            <div className="attachments-with-title-wrapper">
                <form>
                    <div className="t-form-controls-wrapper">
                        <div className="attachment-section-header">Lab Attachments</div>
                        <div className="attachment-input-wrapper">
                            <div className={'attachment-input-section'}>
                                <InputComponent
                                    type={"text"}
                                    name={"title"}
                                    label={"Attachment Name"}
                                    placeholder={"Enter Attachment Name"}
                                    value={labAttachmentTitle}
                                    onChange={(value: any) => {
                                        setLabAttachmentTitle(value.replace(/\s/g, ''));
                                    }}
                                    onkeyDownPreventDefault={true}
                                />
                                {
                                    labAttachmentTitle.length > 100 &&
                                    <ErrorComponent errorText={'Attachment name should be less than 100 characters'}/>
                                }
                            </div>
                            <input type={"file"}
                                   className={"display-none"}
                                   ref={labAttachmentFileRef}
                                   accept={"image/jpeg,image/gif,image/png,application/pdf,image/x-eps"}
                                   onChange={(event) => {
                                       handleLabAttachmentUpload(event);
                                   }}
                            />
                            <ButtonComponent
                                className="mrg-left-20"
                                disabled={labAttachmentTitle?.length === 0 || labAttachmentTitle?.length > 100}
                                color={"secondary"}
                                variant={"outlined"}
                                fullWidth={false}
                                size={'large'}
                                handleClick={() => {
                                    labAttachmentFileRef.current?.click()
                                }}>
                                Choose
                            </ButtonComponent>
                        </div>

                        <div className="attachment-list-section">
                            <div className="attachment-header">Uploaded Attachments:</div>
                            {labAttachmentList?.length > 0 &&
                                <div className="attachment-list-wrapper">
                                    {labAttachmentList?.map((attachment: any) =>
                                        <div key={CommonService.getUUID()}>
                                            <AttachmentViewDownloadComponent
                                                attachmentTitle={attachment.attachmentName}
                                                attachment={attachment}
                                                showDeleteOption={true}
                                                onClick={(id: any) => handleAttachmentRemove(id, 'lab')}
                                            />
                                        </div>
                                    )}
                                </div>
                            }
                            {labAttachmentList?.length === 0 &&
                                <FeedbackComponent className={'mrg-top-15'}>No attachment have been
                                    uploaded.</FeedbackComponent>
                            }
                        </div>
                    </div>
                    <div className='form-action-section-edit'>
                        <ButtonComponent
                            className={"width-25 "}
                            type="button"
                            color={"secondary"}
                            variant={"outlined"}
                            fullWidth={false}
                            handleClick={() => onEditCancel()}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            className={"width-25"}
                            type="button"
                            color={"primary"}
                            variant={"contained"}
                            fullWidth={false}
                            disabled={!(labAttachmentList?.length > 0 && isEdited)}
                            handleClick={() => onUpdateComplete(labAttachmentList)}
                        >
                            Save
                        </ButtonComponent>
                    </div>
                </form>
            </div>

            <DialogComponent open={showLabAttachmentPreviewDialog}
                             maxWidth={'md'}
                             title={labAttachmentTitle}
                             isShowCancel={true}
                             onClose={() => {
                                 setLabAttachmentTitle("");
                                 setShowLabAttachmentPreviewDialog(false)
                             }}
                             className={"attachment-preview-dialog"}>
                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    {labAttachmentFile && labAttachmentFile?.type === 'application/pdf' ?
                        <PdfPreviewComponent
                            attachmentFile={labAttachmentFile}
                            isFile={isNewLabAttachment}
                        />
                        :
                        <FilePreviewerComponent
                            attachmentFile={labAttachmentFile}
                            isFile={isNewLabAttachment}
                        />
                    }
                </div>
                {labAttachmentFile && <div className="uploaded-attachment-file-name">
                    <span className="font-weight-bold"> Uploaded File Name:&nbsp;</span>
                    <span>{labAttachmentFile?.name}</span>
                </div>
                }
                <div className="attachment-preview-dialog-footer">
                    <ButtonComponent variant={"outlined"}
                                     id="cancel_btn"
                                     fullWidth={true}
                                     color={'primary'}
                                     disabled={isLabAttachmentBeingUploaded}
                                     handleClick={() => {
                                         labAttachmentFileRef.current?.click()
                                     }}
                    >
                        Re-upload
                    </ButtonComponent>&nbsp;
                    {
                        isContinueVisible &&
                        <ButtonComponent variant={"contained"}
                                         id="continue_btn"
                                         color={'primary'}
                                         fullWidth={true}
                                         isLoading={isLabAttachmentBeingUploaded}
                                         handleClick={onLabAttachmentSubmit}>
                            Continue
                        </ButtonComponent>
                    }
                </div>
            </DialogComponent>
        </div>
    );

};

export default LabAttachmentsEditComponent;
